import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import { formatearNumeroCL, formatoMiles, desformatearMiles } from '../utils/formatUtils';
import { parsearObservaciones, etiquetaTipoEnvio } from '../utils/observacionesUtils';
import { showConfirm } from '../lib/swal';
import EnvioEstadoStepper from '../components/EnvioEstadoStepper';
import AyudaModal from '../components/AyudaModal';
import Tooltip from '../components/Tooltip';
import BotonCargando from '../components/BotonCargando';
import TablaScroll from '../components/TablaScroll';
import { aplicarRebajaPorTipoToner as aplicarRebajaTipo } from '../utils/rendimientoUtils';

// Cache management utility
const clearPageCache = () => {
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        caches.delete(cacheName);
      });
    });
  }
};

export default function EnvioCliente() {
  const user = useAuthStore(state => state.user);
  const puede = useAuthStore(state => state.puede);

  // Estados de datos
  const [clientes, setClientes] = useState([]);
  const [toners, setToners] = useState([]);
  const [tiposToner, setTiposToner] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [tonersPendientes, setTonersPendientes] = useState([]);

  // Estados del wizard
  const [pasoActual, setPasoActual] = useState(1);
  const [vistaActual, setVistaActual] = useState('wizard'); // 'wizard', 'pendientes', 'historial'

  // Formulario - Paso 1
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [clienteBusqueda, setClienteBusqueda] = useState('');
  const [tonerSeleccionado, setTonerSeleccionado] = useState('');
  const [tipoTonerSeleccionado, setTipoTonerSeleccionado] = useState('');

  // Formulario - Paso 2
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [copiasRealizadas, setCopiasRealizadas] = useState('');
  const [esEnvioAdelantado, setEsEnvioAdelantado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [procesandoAprobacion, setProcesandoAprobacion] = useState(false);

  // Formulario - Paso 3
  const [cantidadDespachar, setCantidadDespachar] = useState('');
  const [rendimientoPersonalizado, setRendimientoPersonalizado] = useState('');
  const [observaciones, setObservaciones] = useState('');

  // Cálculos
  const [cantidadCalculada, setCantidadCalculada] = useState(0);
  const [rendimientoEsperado, setRendimientoEsperado] = useState(0);
  const [cantidadPendienteCliente, setCantidadPendienteCliente] = useState(0);
  const [enviosAdelantadosDelMes, setEnviosAdelantadosDelMes] = useState(0);

  // Modal de aprobación (admin)
  const [modalAprobacion, setModalAprobacion] = useState({ isOpen: false, registro: null });
  const [tipoTonerAprobacion, setTipoTonerAprobacion] = useState('');
  const [cantidadAprobacion, setCantidadAprobacion] = useState('');
  const [diaEnvio, setDiaEnvio] = useState(''); // Día de la semana para coordinar envío

  // Modal de guía (secretaria)
  const [modalGuia, setModalGuia] = useState({ isOpen: false, registro: null });
  const [guiaDespacho, setGuiaDespacho] = useState('');

  // Modal de despacho de pendientes
  const [modalDespachoPendientes, setModalDespachoPendientes] = useState({ isOpen: false, cliente: null, pendientes: [] });
  const [cantidadesDespacho, setCantidadesDespacho] = useState({});
  const [guiasPendientes, setGuiasPendientes] = useState({});

  // Resumen de tóners pendientes por cliente
  const [resumenPendientes, setResumenPendientes] = useState([]);

  // Estado para controlar si ya se mostró la notificación
  const [notificacionMostrada, setNotificacionMostrada] = useState(false);

  // Filtrado de clientes mejorado
  const clientesFiltrados = clientes.filter(cliente =>
    cliente.nombre.toLowerCase().includes(clienteBusqueda.toLowerCase())
  ).slice(0, 10);

  useEffect(() => {
    clearPageCache();
    cargarDatos();

    const interval = setInterval(() => {
      // Solo refrescar si la pestaña está visible (ahorra cuota de Supabase)
      if (document.visibilityState !== 'visible') return;
      cargarTonersPendientes();
      cargarResumenPendientes();
      cargarHistorial();
    }, 180000); // 3 minutos. Con varios usuarios conectados, un intervalo corto
                // consume rápidamente la cuota de transferencia del plan gratuito
                // de Supabase (5 GB/mes). Las acciones del usuario refrescan los
                // datos al instante, así que este refresco solo detecta cambios
                // hechos por otras personas.

    return () => clearInterval(interval);
  }, []);

  const cargarDatos = async () => {
    const { data: clientesData } = await supabase
      .from('clientes')
      .select('*')
      .order('nombre');
    setClientes(clientesData || []);

    const { data: tonersData } = await supabase
      .from('toners')
      .select('*')
      .order('modelo');
    setToners(tonersData || []);

    const { data: tiposData } = await supabase
      .from('tipos_toner')
      .select('*')
      .order('nombre');
    setTiposToner(tiposData || []);

    if (puede('aprobar_envios')) {
      cargarPendientes();
    }
    cargarHistorial();
    cargarTonersPendientes();
    cargarResumenPendientes();
  };

  const cargarPendientes = async () => {
    const { data, error } = await supabase
      .from('envios_cliente_pendientes')
      .select(`
        *,
        clientes(nombre),
        toners(modelo, rendimiento),
        tipos_toner(nombre)
      `)
      .order('creado_el', { ascending: false });

    if (!error) {
      setPendientes(data || []);
    }
  };

  const cargarHistorial = async () => {
    if (user.role === 'secretaria') {
      const { data: aprobados, error: errorAprobados } = await supabase
        .from('envios_cliente_aprobados')
        .select(`
          *,
          clientes(nombre),
          toners(modelo, rendimiento),
          tipos_toner(nombre)
        `)
        .order('creado_el', { ascending: false });

      if (!errorAprobados) {
        setHistorial(aprobados || []);
      }
    } else {
      const { data, error } = await supabase
        .from('envios_cliente_aprobados')
        .select(`
          *,
          clientes(nombre),
          toners(modelo, rendimiento),
          tipos_toner(nombre)
        `)
        .order('creado_el', { ascending: false });

      if (!error) {
        setHistorial(data || []);

        // Notificar a secretaria si hay envíos sin guía (solo una vez al cargar la página)
        if (user.role === 'secretaria' && !notificacionMostrada) {
          const sinGuia = (data || []).filter(h => !h.guia);
          if (sinGuia.length > 0) {
            setTimeout(() => {
              toast.warning(`⚠️ Tienes ${sinGuia.length} envío(s) aprobado(s) esperando guía de despacho. Ve a la pestaña "Historial" para agregarlas.`, {
                duration: 6000,
                position: 'top-center'
              });
              setNotificacionMostrada(true);
            }, 1000);
          }
        }
      }
    }
  };

  const cargarTonersPendientes = async () => {
    const { data, error } = await supabase
      .from('toners_pendientes_cliente')
      .select(`
        *,
        clientes(nombre),
        toners(modelo),
        tipos_toner(nombre)
      `)
      .gt('cantidad_pendiente', 0)
      .order('actualizado_el', { ascending: false });

    if (!error) {
      setTonersPendientes(data || []);
    }
  };

  const cargarResumenPendientes = async () => {
    const { data, error } = await supabase
      .from('toners_pendientes_cliente')
      .select(`
        cliente_id,
        cantidad_pendiente,
        clientes(nombre),
        toners(modelo),
        tipos_toner(nombre)
      `)
      .gt('cantidad_pendiente', 0)
      .order('cantidad_pendiente', { ascending: false });

    if (!error) {
      const resumen = {};
      (data || []).forEach(item => {
        const clienteId = item.cliente_id;
        if (!resumen[clienteId]) {
          resumen[clienteId] = {
            cliente_id: clienteId,  // ✅ AGREGADO: guardar el ID del cliente
            cliente: item.clientes?.nombre,
            totalPendientes: 0,
            detalles: []
          };
        }
        resumen[clienteId].totalPendientes += item.cantidad_pendiente;
        resumen[clienteId].detalles.push({
          toner: item.toners?.modelo,
          tipo: item.tipos_toner?.nombre,
          cantidad: item.cantidad_pendiente
        });
      });

      setResumenPendientes(Object.values(resumen));
    }
  };

  const obtenerPendientesPorCliente = (clienteId) => {
    const pendientesCliente = tonersPendientes.filter(tp => tp.cliente_id === clienteId);
    return pendientesCliente.reduce((total, tp) => total + tp.cantidad_pendiente, 0);
  };

  // La tabla de rebajas por tipo de tóner está en utils/rendimientoUtils.js
  const aplicarRebajaPorTipoToner = (rendimiento, tipoTonerId) =>
    aplicarRebajaTipo(rendimiento, tipoTonerId, tiposToner);

  // (Se eliminó verificarPendientesMesesAnteriores: ya no se bloquean envíos por
  // pendientes de meses anteriores; ahora se suman automáticamente.)

  useEffect(() => {
    if (clienteSeleccionado && tonerSeleccionado && tipoTonerSeleccionado) {
      const pendiente = tonersPendientes.find(tp =>
        tp.cliente_id === clienteSeleccionado &&
        tp.toner_id === parseInt(tonerSeleccionado) &&
        tp.tipo_toner_id === parseInt(tipoTonerSeleccionado)
      );
      setCantidadPendienteCliente(pendiente?.cantidad_pendiente || 0);
    } else {
      setCantidadPendienteCliente(0);
    }
  }, [clienteSeleccionado, tonerSeleccionado, tipoTonerSeleccionado, tonersPendientes]);

  const obtenerEnviosAdelantadosDelMes = async (clienteId, tonerId, tipoTonerId, mesSeleccionado) => {
    if (!clienteId || !tonerId || !tipoTonerId || !mesSeleccionado) return 0;

    const añoActual = new Date().getFullYear();
    const fechaMes = `${añoActual}-${mesSeleccionado.padStart(2, '0')}-01`;

    try {
      // Primero buscar en el historial (envíos con guía completa)
      const { data: historial } = await supabase
        .from('envios_cliente_historial')
        .select('cantidad_despachada, observaciones')
        .eq('cliente_id', clienteId)
        .eq('toner_id', tonerId)
        .eq('tipo_toner_id', tipoTonerId)
        .eq('mes_correspondiente', fechaMes);

      // Si hay envíos en historial, usar solo esos (ya tienen guía)
      if (historial && historial.length > 0) {
        const totalAdelantado = historial
          .filter(envio =>
            envio.observaciones &&
            (envio.observaciones.includes('ENVÍO POR ADELANTADO') ||
             envio.observaciones.includes('SOLICITUD ENVÍO ADELANTADO'))
          )
          .reduce((total, envio) => total + envio.cantidad_despachada, 0);
        return totalAdelantado;
      }

      // Si no hay en historial, buscar en aprobados (sin guía aún)
      const { data: aprobados } = await supabase
        .from('envios_cliente_aprobados')
        .select('cantidad_despachada, observaciones')
        .eq('cliente_id', clienteId)
        .eq('toner_id', tonerId)
        .eq('tipo_toner_id', tipoTonerId)
        .eq('mes_correspondiente', fechaMes);

      const totalAdelantado = (aprobados || [])
        .filter(envio =>
          envio.observaciones &&
          (envio.observaciones.includes('ENVÍO POR ADELANTADO') ||
           envio.observaciones.includes('SOLICITUD ENVÍO ADELANTADO'))
        )
        .reduce((total, envio) => total + envio.cantidad_despachada, 0);

      return totalAdelantado;
    } catch (error) {
      console.error('Error al obtener envíos adelantados:', error);
      return 0;
    }
  };

  useEffect(() => {
    const cargarEnviosAdelantados = async () => {
      if (clienteSeleccionado && tonerSeleccionado && tipoTonerSeleccionado && mesSeleccionado && !esEnvioAdelantado) {
        const enviosAdelantados = await obtenerEnviosAdelantadosDelMes(
          clienteSeleccionado,
          parseInt(tonerSeleccionado),
          parseInt(tipoTonerSeleccionado),
          mesSeleccionado
        );
        setEnviosAdelantadosDelMes(enviosAdelantados);
      } else {
        setEnviosAdelantadosDelMes(0);
      }
    };

    cargarEnviosAdelantados();
  }, [clienteSeleccionado, tonerSeleccionado, tipoTonerSeleccionado, mesSeleccionado, esEnvioAdelantado]);

  useEffect(() => {
    if (tonerSeleccionado && tipoTonerSeleccionado) {
      const toner = toners.find(t => t.id === parseInt(tonerSeleccionado));
      const tipoToner = tiposToner.find(t => t.id === parseInt(tipoTonerSeleccionado));

      if (toner && tipoToner) {
        if (toner.modelo === 'T06' && tipoToner.nombre.toLowerCase() === 'original') {
          setRendimientoPersonalizado('15000');
        }
      }
    }
  }, [tonerSeleccionado, tipoTonerSeleccionado, toners, tiposToner]);

  useEffect(() => {
    if (copiasRealizadas && tonerSeleccionado && tipoTonerSeleccionado && !esEnvioAdelantado) {
      const toner = toners.find(t => t.id === parseInt(tonerSeleccionado));
      if (toner) {
        const rendimientoBase = rendimientoPersonalizado ?
          parseInt(rendimientoPersonalizado) :
          aplicarRebajaPorTipoToner(toner.rendimiento, parseInt(tipoTonerSeleccionado));

        setRendimientoEsperado(rendimientoBase);

        // Remover formato de miles antes de convertir a número
        const copias = parseInt(desformatearMiles(copiasRealizadas)) || 0;
        const cantidadNecesaria = Math.floor(copias / rendimientoBase);

        // Cálculo: cantidad necesaria + pendientes - adelantados
        const cantidadTotal = Math.max(0, cantidadNecesaria + cantidadPendienteCliente - enviosAdelantadosDelMes);
        setCantidadCalculada(cantidadTotal);

        // El campo "Cantidad a Despachar" arranca en el TOTAL calculado (incluye pendientes),
        // para que coincida con el desglose que ve el usuario. El admin puede bajarlo manualmente
        // si quiere despachar menos; al hacerlo, esa decisión se respeta (no se vuelve a sobrescribir
        // con el mismo valor). La secretaria siempre usa el total.
        if (user.role === 'secretaria') {
          setCantidadDespachar(cantidadTotal.toString());
        } else {
          // Para admin: sincronizar con el total mientras el campo no haya sido bajado
          // manualmente por debajo del total (caso típico: campo vacío, en 0, o aún igual a un
          // total anterior que cambió porque variaron copias/pendientes).
          setCantidadDespachar(prev => {
            const prevNum = parseInt(prev);
            if (!prev || prev === '0' || isNaN(prevNum) || prevNum > cantidadTotal) {
              return cantidadTotal.toString();
            }
            return prev; // respetar un valor menor elegido a propósito por el admin
          });
        }
      }
    } else if (esEnvioAdelantado) {
      setCantidadCalculada(0);
      setRendimientoEsperado(0);
      if (!cantidadDespachar) {
        setCantidadDespachar('');
      }
    } else {
      setCantidadCalculada(0);
      setRendimientoEsperado(0);
      setCantidadDespachar('');
    }
  }, [copiasRealizadas, tonerSeleccionado, tipoTonerSeleccionado, rendimientoPersonalizado, cantidadPendienteCliente, enviosAdelantadosDelMes, toners, tiposToner, esEnvioAdelantado, user.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar observaciones obligatorias
    // Para secretarias: SIEMPRE obligatorias (envío normal o adelantado)
    // Para admins: Solo obligatorias en envíos normales
    if (user.role === 'secretaria' && !observaciones.trim()) {
      toast.error('Las observaciones son obligatorias. Por favor, ingresa una descripción del envío.', { duration: 4000 });
      return;
    } else if (!esEnvioAdelantado && !observaciones.trim()) {
      toast.error('Las observaciones son obligatorias para envíos normales. Por favor, ingresa una descripción del envío.', { duration: 4000 });
      return;
    }

    // NOTA: Antes aquí se bloqueaba el envío si el cliente tenía pendientes de meses
    // anteriores. Ese candado se eliminó: ahora los pendientes se SUMAN automáticamente
    // a la cantidad calculada (ver cálculo en el useEffect de cantidadCalculada) y se
    // saldan cuando el administrador aprueba el envío. El desglose se muestra al usuario
    // en el Paso 2 y se guarda en las observaciones para trazabilidad.

    if (esEnvioAdelantado) {
      // Para adelantado solo validamos cliente, tóner, tipo y mes (admin decide cantidad)
      if (!clienteSeleccionado || !tonerSeleccionado || !tipoTonerSeleccionado || !mesSeleccionado) {
        toast.error('Para envíos por adelantado: Cliente, Tóner, Tipo y Mes son obligatorios', { duration: 4000 });
        return;
      }
    } else {
      if (puede('aprobar_envios')) {
        if (!clienteSeleccionado || !tonerSeleccionado || !tipoTonerSeleccionado || !copiasRealizadas || !mesSeleccionado || !cantidadDespachar) {
          toast.error('Todos los campos son obligatorios, incluyendo las observaciones');
          return;
        }
      } else {
        if (!clienteSeleccionado || !tonerSeleccionado || !tipoTonerSeleccionado || !copiasRealizadas || !mesSeleccionado) {
          toast.error('Cliente, Tóner, Tipo, Copias Realizadas, Mes y Observaciones son obligatorios');
          return;
        }
      }
    }

    if (!user || !user.id) {
      toast.error('Error de autenticación. Por favor, inicia sesión nuevamente.');
      return;
    }

    // NOTA: Se eliminó el bloqueo que impedía registrar cuando el cliente tenía pendientes.
    // Ahora los pendientes se suman al envío y se saldan al aprobar (Opción B - suma visible).

    const añoActual = new Date().getFullYear();
    const fechaMes = `${añoActual}-${mesSeleccionado.padStart(2, '0')}-01`;

    const cantidadAUsar = esEnvioAdelantado
      ? parseInt(cantidadDespachar)
      : (user.role === 'secretaria' ? cantidadCalculada : parseInt(cantidadDespachar));

    setGuardando(true);
    try {
      if (esEnvioAdelantado) {
        const clienteNombre = clientes.find(c => c.id === clienteSeleccionado)?.nombre;
        const tonerNombre = toners.find(t => t.id === parseInt(tonerSeleccionado))?.modelo;

        const registroData = {
          cliente_id: clienteSeleccionado,
          toner_id: parseInt(tonerSeleccionado),
          tipo_toner_id: parseInt(tipoTonerSeleccionado),
          copias_realizadas: 0,
          cantidad_calculada: 0, // Sin cálculo, admin decidirá
          cantidad_despachada: 0, // Admin DEBE decidir la cantidad (no hay valor por defecto)
          rendimiento_esperado: 0,
          rendimiento_personalizado: null,
          mes_correspondiente: fechaMes,
          observaciones: `SOLICITUD ENVÍO ADELANTADO - Cliente: ${clienteNombre} - Tóner: ${tonerNombre} - Mes: ${mesNombre} ${añoActual}${observaciones.trim() ? ' - ' + observaciones.trim() : ''}`,
          creado_por: user.id
        };

        const { error } = await supabase
          .from('envios_cliente_pendientes')
          .insert(registroData);

        if (error) {
          console.error('Error al insertar envío por adelantado:', error);
          toast.error('Error al guardar el envío por adelantado: ' + error.message);
          return;
        }

        toast.success(`✅ Solicitud de envío por adelantado enviada al administrador para ${mesNombre} ${añoActual}. El admin decidirá la cantidad a despachar.`, { duration: 5000 });
      } else {
        // Construir un detalle del desglose para trazabilidad cuando hay pendientes incluidos
        const cantidadNecesariaMes = Math.max(0, cantidadCalculada - cantidadPendienteCliente + enviosAdelantadosDelMes);
        let observacionesFinales = observaciones.trim() || '';
        if (cantidadPendienteCliente > 0) {
          const detallePendientes = `[DESGLOSE] ${cantidadNecesariaMes} del mes + ${cantidadPendienteCliente} pendiente(s) anterior(es) = ${cantidadCalculada} total`;
          observacionesFinales = observacionesFinales
            ? `${observacionesFinales} | ${detallePendientes}`
            : detallePendientes;
        }

        const registroData = {
          cliente_id: clienteSeleccionado,
          toner_id: parseInt(tonerSeleccionado),
          tipo_toner_id: parseInt(tipoTonerSeleccionado),
          copias_realizadas: parseInt(desformatearMiles(copiasRealizadas)), // ✅ Desformatear antes de parseInt
          cantidad_calculada: cantidadCalculada,
          cantidad_despachada: cantidadAUsar,
          rendimiento_esperado: rendimientoEsperado,
          rendimiento_personalizado: rendimientoPersonalizado ? parseInt(rendimientoPersonalizado) : null,
          mes_correspondiente: fechaMes,
          observaciones: observacionesFinales || null,
          creado_por: user.id
        };

        const { error } = await supabase
          .from('envios_cliente_pendientes')
          .insert(registroData);

        if (error) {
          console.error('Error al insertar envío normal:', error);
          toast.error('Error al guardar el envío: ' + error.message);
          return;
        }

        toast.success('Envío registrado correctamente y enviado para aprobación');
      }

      // Limpiar formulario y volver al paso 1
      resetearFormulario();
      cargarDatos();
    } finally {
      setGuardando(false);
    }
  };

  const resetearFormulario = () => {
    setClienteSeleccionado('');
    setClienteBusqueda('');
    setTonerSeleccionado('');
    setTipoTonerSeleccionado('');
    setCopiasRealizadas('');
    setMesSeleccionado('');
    setCantidadDespachar('');
    setRendimientoPersonalizado('');
    setObservaciones('');
    setEsEnvioAdelantado(false);
    setCantidadCalculada(0);
    setRendimientoEsperado(0);
    setPasoActual(1);
  };

  const aprobarEnvio = async (registro) => {
    const tipoTonerFinal = tipoTonerAprobacion || registro.tipo_toner_id;
    const cantidadFinal = cantidadAprobacion ? parseInt(cantidadAprobacion) : registro.cantidad_despachada;

    if (cantidadFinal === null || cantidadFinal === undefined || cantidadFinal < 0) {
      toast.error('Debe ingresar una cantidad válida a despachar', { duration: 4000 });
      return;
    }

    // Detectar tipo de envío
    const esDespachoPendientes = registro.observaciones && registro.observaciones.includes('SOLICITUD DESPACHO PENDIENTES');
    const esEnvioAdelantado = registro.observaciones && registro.observaciones.includes('SOLICITUD ENVÍO ADELANTADO');

    // Solo validar cantidad máxima para envíos normales (no adelantados ni pendientes)
    if (!esDespachoPendientes && !esEnvioAdelantado && cantidadFinal > registro.cantidad_calculada) {
      toast.error('La cantidad a despachar no puede ser mayor a la calculada', { duration: 4000 });
      return;
    }

    // Agregar día de envío a las observaciones si fue seleccionado
    let observacionesFinal = registro.observaciones || '';
    if (diaEnvio) {
      observacionesFinal = observacionesFinal
        ? `${observacionesFinal} | 📅 DÍA DE ENVÍO: ${diaEnvio}`
        : `📅 DÍA DE ENVÍO: ${diaEnvio}`;
    }

    setProcesandoAprobacion(true);
    try {
      // Intentar primero la función atómica de Postgres (todo-o-nada).
      const { data, error } = await supabase.rpc('aprobar_envio_cliente', {
        p_registro_id: registro.id,
        p_cantidad_final: cantidadFinal,
        p_tipo_toner_id: tipoTonerFinal,
        p_observaciones: observacionesFinal,
        p_aprobado_por: user.id
      });

      if (error) {
        // Si la función todavía no existe en la base (ej. no se ha corrido el SQL),
        // usar el método tradicional como respaldo para no bloquear la operación.
        if (esErrorFuncionInexistente(error)) {
          console.warn('RPC aprobar_envio_cliente no disponible, usando método tradicional.');
          await aprobarEnvioLegacy(registro, tipoTonerFinal, cantidadFinal, observacionesFinal, esDespachoPendientes);
        } else {
          console.error('Error al aprobar envío (RPC):', error);
          toast.error('Error al aprobar envío: ' + error.message);
          return;
        }
      } else {
        toast.success(data?.mensaje || 'Envío aprobado correctamente.');
      }

      setModalAprobacion({ isOpen: false, registro: null });
      setTipoTonerAprobacion('');
      setCantidadAprobacion('');
      setDiaEnvio(''); // Limpiar día de envío
      cargarDatos();
    } finally {
      setProcesandoAprobacion(false);
    }
  };

  // Detecta si un error de Supabase se debe a que la función RPC no existe.
  const esErrorFuncionInexistente = (error) => {
    if (!error) return false;
    // Postgres 42883 = undefined_function; también cubrimos PGRST202 (PostgREST no encuentra la función)
    return error.code === '42883'
      || error.code === 'PGRST202'
      || /function .* does not exist/i.test(error.message || '')
      || /could not find the function/i.test(error.message || '');
  };

  // Método tradicional (respaldo): se usa solo si la función atómica no está instalada.
  // NOTA: hace varias escrituras seguidas sin transacción; por eso preferimos el RPC.
  const aprobarEnvioLegacy = async (registro, tipoTonerFinal, cantidadFinal, observacionesFinal, esDespachoPendientes) => {
    const { error: errorAprobado } = await supabase.from('envios_cliente_aprobados').insert({
      cliente_id: registro.cliente_id,
      toner_id: registro.toner_id,
      tipo_toner_id: tipoTonerFinal,
      copias_realizadas: registro.copias_realizadas,
      cantidad_calculada: registro.cantidad_calculada,
      cantidad_despachada: cantidadFinal,
      rendimiento_esperado: registro.rendimiento_esperado,
      rendimiento_personalizado: registro.rendimiento_personalizado,
      mes_correspondiente: registro.mes_correspondiente,
      observaciones: observacionesFinal,
      creado_por: registro.creado_por,
      aprobado_por: user.id
    });

    if (errorAprobado) {
      toast.error('Error al aprobar envío');
      return;
    }

    await supabase.from('envios_cliente_historial').insert({
      cliente_id: registro.cliente_id,
      toner_id: registro.toner_id,
      tipo_toner_id: tipoTonerFinal,
      copias_realizadas: registro.copias_realizadas,
      cantidad_calculada: registro.cantidad_calculada,
      cantidad_despachada: cantidadFinal,
      rendimiento_esperado: registro.rendimiento_esperado,
      rendimiento_personalizado: registro.rendimiento_personalizado,
      mes_correspondiente: registro.mes_correspondiente,
      observaciones: observacionesFinal,
      resultado: 'aprobado',
      creado_por: registro.creado_por,
      aprobado_por: user.id
    });

    // Manejar pendientes según el tipo de solicitud
    if (esDespachoPendientes) {
      // Es despacho de pendientes: REDUCIR la cantidad pendiente
      const { data: existentes, error: errorPendientes } = await supabase
        .from('toners_pendientes_cliente')
        .select('*')
        .eq('cliente_id', registro.cliente_id)
        .eq('toner_id', registro.toner_id)
        .eq('tipo_toner_id', tipoTonerFinal)
        .order('actualizado_el', { ascending: true })
        .limit(1);

      if (errorPendientes) {
        console.error('Error al buscar pendientes:', errorPendientes);
        toast.error('Error al actualizar pendientes');
        return;
      }

      if (existentes && existentes.length > 0) {
        const existente = existentes[0];
        const nuevaCantidadPendiente = existente.cantidad_pendiente - cantidadFinal;

        if (nuevaCantidadPendiente > 0) {
          await supabase
            .from('toners_pendientes_cliente')
            .update({
              cantidad_pendiente: nuevaCantidadPendiente,
              actualizado_el: new Date().toISOString()
            })
            .eq('id', existente.id);
        } else {
          // Si no quedan pendientes, eliminar el registro
          await supabase
            .from('toners_pendientes_cliente')
            .delete()
            .eq('id', existente.id);
        }
      }

      await supabase.from('envios_cliente_pendientes').delete().eq('id', registro.id);

      toast.success(`Despacho de pendientes aprobado. ${cantidadFinal} tóner(es) despachados.`);
    } else {
      // Es envío normal: si se aprueba menos, AUMENTAR pendientes
      // La cantidad_calculada YA incluye los pendientes anteriores (se sumaron al crear el envío).
      const cantidadPendiente = registro.cantidad_calculada - cantidadFinal;

      // Paso 1: eliminar pendientes anteriores ya contemplados en este envío
      const { data: pendientesPrevios } = await supabase
        .from('toners_pendientes_cliente')
        .select('id')
        .eq('cliente_id', registro.cliente_id)
        .eq('toner_id', registro.toner_id)
        .eq('tipo_toner_id', tipoTonerFinal);

      if (pendientesPrevios && pendientesPrevios.length > 0) {
        await supabase
          .from('toners_pendientes_cliente')
          .delete()
          .in('id', pendientesPrevios.map(p => p.id));
      }

      // Paso 2: si quedó un faltante, registrarlo como un único pendiente consolidado
      if (cantidadPendiente > 0) {
        await supabase
          .from('toners_pendientes_cliente')
          .insert({
            cliente_id: registro.cliente_id,
            toner_id: registro.toner_id,
            tipo_toner_id: tipoTonerFinal,
            cantidad_pendiente: cantidadPendiente,
            mes_acumulado: registro.mes_correspondiente,
            actualizado_el: new Date().toISOString()
          });
      }

      await supabase.from('envios_cliente_pendientes').delete().eq('id', registro.id);

      toast.success(`Envío aprobado correctamente. ${cantidadPendiente > 0 ? `${cantidadPendiente} tóner(es) quedaron como pendientes.` : 'Sin pendientes restantes.'}`);
    }
  };

  const rechazarEnvio = async (registro) => {
    setProcesandoAprobacion(true);
    try {
      // Intentar primero la función atómica de Postgres.
      const { data, error } = await supabase.rpc('rechazar_envio_cliente', {
        p_registro_id: registro.id,
        p_aprobado_por: user.id
      });

      if (error) {
        if (esErrorFuncionInexistente(error)) {
          console.warn('RPC rechazar_envio_cliente no disponible, usando método tradicional.');
          await rechazarEnvioLegacy(registro);
        } else {
          console.error('Error al rechazar envío (RPC):', error);
          toast.error('Error al rechazar envío: ' + error.message);
          return;
        }
      } else {
        toast.success(data?.mensaje || 'Envío rechazado');
      }

      cargarDatos();
    } finally {
      setProcesandoAprobacion(false);
    }
  };

  // Método tradicional de rechazo (respaldo): se usa solo si el RPC no está instalado.
  const rechazarEnvioLegacy = async (registro) => {
    await supabase.from('envios_cliente_historial').insert({
      cliente_id: registro.cliente_id,
      toner_id: registro.toner_id,
      tipo_toner_id: registro.tipo_toner_id,
      copias_realizadas: registro.copias_realizadas,
      cantidad_calculada: registro.cantidad_calculada,
      cantidad_despachada: registro.cantidad_despachada,
      rendimiento_esperado: registro.rendimiento_esperado,
      rendimiento_personalizado: registro.rendimiento_personalizado,
      mes_correspondiente: registro.mes_correspondiente,
      observaciones: registro.observaciones,
      resultado: 'rechazado',
      creado_por: registro.creado_por,
      aprobado_por: user.id
    });

    await supabase.from('envios_cliente_pendientes').delete().eq('id', registro.id);

    toast.success('Envío rechazado');
  };

  const agregarGuia = async () => {
    if (!guiaDespacho.trim()) {
      toast.error('Debe ingresar una guía de despacho');
      return;
    }

    const registro = modalGuia.registro;

    try {
      const { error: errorUpdate } = await supabase
        .from('envios_cliente_aprobados')
        .update({
          guia: guiaDespacho.trim()
        })
        .eq('id', registro.id);

      if (errorUpdate) {
        console.error('Error al actualizar guía:', errorUpdate);
        toast.error('Error al agregar guía: ' + errorUpdate.message);
        return;
      }

      toast.success('Guía agregada correctamente - Envío marcado como despachado');
      setModalGuia({ isOpen: false, registro: null });
      setGuiaDespacho('');

      cargarDatos();
    } catch (error) {
      console.error('Error en agregarGuia:', error);
      toast.error('Error inesperado al agregar guía');
    }
  };

  const limpiarRegistrosDespachados = async () => {
    const confirmado = await showConfirm(
      '¿Limpiar la vista de registros despachados?',
      'Los registros permanecerán guardados en el historial completo. Solo se ocultan de esta vista.',
      { confirmText: 'Sí, limpiar vista' }
    );
    if (!confirmado) {
      return;
    }

    try {
      const { data: registrosDespachados, error: errorSelect } = await supabase
        .from('envios_cliente_aprobados')
        .select('*')
        .not('guia', 'is', null);

      if (errorSelect) {
        console.error('Error al obtener registros despachados:', errorSelect);
        toast.error('Error al obtener registros: ' + errorSelect.message);
        return;
      }

      if (!registrosDespachados || registrosDespachados.length === 0) {
        toast.info('No hay registros despachados para limpiar');
        return;
      }

      for (const registro of registrosDespachados) {
        const { data: historialExistente } = await supabase
          .from('envios_cliente_historial')
          .select('id')
          .eq('cliente_id', registro.cliente_id)
          .eq('toner_id', registro.toner_id)
          .eq('tipo_toner_id', registro.tipo_toner_id)
          .eq('mes_correspondiente', registro.mes_correspondiente)
          .eq('cantidad_despachada', registro.cantidad_despachada)
          .single();

        if (historialExistente) {
          await supabase
            .from('envios_cliente_historial')
            .update({
              observaciones: registro.observaciones + (registro.guia ? ` | GUÍA: ${registro.guia}` : '')
            })
            .eq('id', historialExistente.id);
        }
      }

      const { error: errorDelete } = await supabase
        .from('envios_cliente_aprobados')
        .delete()
        .not('guia', 'is', null);

      if (errorDelete) {
        console.error('Error al limpiar registros:', errorDelete);
        toast.error('Error al limpiar registros: ' + errorDelete.message);
        return;
      }

      toast.success(`Vista de registros despachados limpiada. ${registrosDespachados.length} guías preservadas en el historial.`);

      cargarDatos();
    } catch (error) {
      console.error('Error en limpiarRegistrosDespachados:', error);
      toast.error('Error inesperado al limpiar registros');
    }
  };

  const solicitarDespachoPendientes = async () => {
    const cliente = modalDespachoPendientes.cliente;
    if (!cliente) {
      toast.error('Error: No se pudo identificar el cliente');
      return;
    }

    const pendientesCliente = tonersPendientes.filter(tp => tp.cliente_id === cliente.id);

    if (pendientesCliente.length === 0) {
      toast.error('No hay tóners pendientes para este cliente');
      return;
    }

    try {
      let solicitudesCreadas = 0;
      let erroresEncontrados = 0;

      // Crear solicitudes de despacho para TODOS los tóners pendientes del cliente
      // La secretaria solo envía, el admin decide cuánto despachar
      for (const tp of pendientesCliente) {
        // Crear registro en envios_cliente_pendientes para que el admin lo apruebe
        const { error } = await supabase
          .from('envios_cliente_pendientes')
          .insert({
            cliente_id: tp.cliente_id,
            toner_id: tp.toner_id,
            tipo_toner_id: tp.tipo_toner_id,
            copias_realizadas: 0,
            cantidad_calculada: tp.cantidad_pendiente, // Enviar la cantidad total pendiente
            cantidad_despachada: tp.cantidad_pendiente, // El admin podrá modificar esto
            rendimiento_esperado: 0,
            rendimiento_personalizado: null,
            mes_correspondiente: tp.mes_acumulado,
            observaciones: `SOLICITUD DESPACHO PENDIENTES - Mes original: ${formatearFecha(tp.mes_acumulado)} - Cantidad pendiente total: ${tp.cantidad_pendiente}`,
            creado_por: user.id
          })
          .select();

        if (error) {
          console.error('Error al crear solicitud:', error);
          toast.error('Error al crear solicitud: ' + error.message);
          erroresEncontrados++;
        } else {
          solicitudesCreadas++;
        }
      }

      if (solicitudesCreadas > 0) {
        toast.success(`${solicitudesCreadas} solicitud(es) enviada(s) al administrador para aprobación`);
        setModalDespachoPendientes({ isOpen: false, cliente: null, pendientes: [] });
        setCantidadesDespacho({});
        setGuiasPendientes({});
        await cargarDatos();
      } else if (erroresEncontrados > 0) {
        toast.error('No se pudo crear ninguna solicitud. Revisa los errores.');
      }
    } catch (error) {
      console.error('Error al solicitar despacho de pendientes:', error);
      toast.error('Error al solicitar despacho de pendientes: ' + error.message);
    }
  };

  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr + 'T00:00:00');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[fecha.getMonth()]} ${fecha.getFullYear()}`;
  };

  const mesesDelAño = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  const validarPaso1 = () => {
    return clienteSeleccionado && tonerSeleccionado && tipoTonerSeleccionado;
  };

  const validarPaso2 = () => {
    if (esEnvioAdelantado) {
      // Para adelantado: mes obligatorio, observaciones obligatorias si es secretaria
      return mesSeleccionado && (user.role !== 'secretaria' || observaciones.trim());
    }
    // Para envío normal: mes, copias y observaciones obligatorias
    return mesSeleccionado && copiasRealizadas && observaciones.trim();
  };

  const avanzarPaso = () => {
    if (pasoActual === 1 && validarPaso1()) {
      setPasoActual(2);
    }
  };

  const retrocederPaso = () => {
    if (pasoActual > 1) {
      setPasoActual(pasoActual - 1);
    }
  };

  const clienteNombre = clientes.find(c => c.id === clienteSeleccionado)?.nombre || '';
  const tonerNombre = toners.find(t => t.id === parseInt(tonerSeleccionado))?.modelo || '';
  const tipoTonerNombre = tiposToner.find(t => t.id === parseInt(tipoTonerSeleccionado))?.nombre || '';
  const mesNombre = mesesDelAño.find(m => m.value === mesSeleccionado)?.label || '';
  const pendientesDelCliente = obtenerPendientesPorCliente(clienteSeleccionado);

  return (
    <div className="p-4 sm:p-6 pt-16 lg:pt-6 ml-0 lg:ml-72 min-h-screen bg-gray-50">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gradient">Gestión de Envíos de Tóner</span>
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Sistema simplificado para gestionar envíos a clientes
            </p>
          </div>
          <AyudaModal titulo="¿Cómo funciona el envío a clientes?">
            <div>
              <p className="font-semibold text-gray-900 mb-1">📝 Paso 1: Crear el envío (secretaria)</p>
              <p className="text-sm">Elige el cliente, el tóner y el mes. El sistema calcula solo cuántos tóner corresponden según las copias realizadas. Luego el envío queda a la espera de aprobación.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">✅ Paso 2: Aprobar (administrador)</p>
              <p className="text-sm">El administrador revisa el envío y confirma cuántos tóner se despacharán. Puede aprobar la cantidad completa o menos.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900 mb-1">🚚 Paso 3: Despachar y agregar guía (secretaria)</p>
              <p className="text-sm">Una vez aprobado, la secretaria agrega el número de guía de despacho. Ahí el envío queda como completado.</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="font-semibold text-orange-900 mb-1">➕ ¿Qué pasa con los tóner pendientes?</p>
              <p className="text-sm text-orange-900">Si en un mes se despachan menos tóner de los que correspondían, la diferencia queda como <strong>pendiente</strong>. La próxima vez que registres un envío para ese cliente, esos pendientes se <strong>suman automáticamente</strong> a lo del mes nuevo, todo en un solo envío. Verás el desglose antes de confirmar (por ejemplo: "3 del mes + 1 pendiente = 4 en total").</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="font-semibold text-purple-900 mb-1">🚀 ¿Y el "Envío por Adelantado"?</p>
              <p className="text-sm text-purple-900">Sirve cuando necesitas enviar un tóner antes de que toque por cálculo (por ejemplo, una urgencia). Ese envío se descuenta después del cálculo del mes que corresponda.</p>
            </div>
          </AyudaModal>
        </div>

        {/* Resumen de tóner pendientes por cliente.
            Siempre visible, para no tener que entrar a crear un registro
            para saber quién tiene pendientes. */}
        {resumenPendientes.length > 0 && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">📌</span>
              <h3 className="font-bold text-orange-900">
                Clientes con tóner pendientes
              </h3>
              <span className="ml-auto text-sm font-semibold text-orange-700">
                {resumenPendientes.reduce((t, r) => t + r.totalPendientes, 0)} en total
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {resumenPendientes
                .slice()
                .sort((a, b) => b.totalPendientes - a.totalPendientes)
                .map((r) => (
                  <span
                    key={r.cliente_id}
                    className="inline-flex items-center gap-1.5 bg-white border border-orange-300 rounded-lg px-3 py-1.5 text-sm"
                  >
                    <span className="text-gray-800">{r.cliente}</span>
                    <span className="font-bold text-orange-700">
                      {r.totalPendientes}
                    </span>
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Pestañas de navegación */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200">
          <button
            onClick={() => setVistaActual('wizard')}
            className={`px-3 sm:px-6 py-3 font-semibold transition text-sm sm:text-base ${
              vistaActual === 'wizard'
                ? 'border-b-4 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            <span className="hidden sm:inline">📝 Crear Envío</span>
            <span className="sm:hidden">📝 Crear</span>
          </button>
          {puede('aprobar_envios') && (
            <button
              onClick={() => setVistaActual('aprobaciones')}
              className={`px-3 sm:px-6 py-3 font-semibold transition relative text-sm sm:text-base ${
                vistaActual === 'aprobaciones'
                  ? 'border-b-4 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <span className="hidden sm:inline">✅ Aprobaciones</span>
              <span className="sm:hidden">✅</span>
              {pendientes.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {pendientes.length}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setVistaActual('historial')}
            className={`px-3 sm:px-6 py-3 font-semibold transition relative text-sm sm:text-base ${
              vistaActual === 'historial'
                ? 'border-b-4 border-green-600 text-green-600'
                : 'text-gray-600 hover:text-green-600'
            }`}
          >
            <span className="hidden sm:inline">📊 Historial</span>
            <span className="sm:hidden">📊</span>
            {user.role === 'secretaria' && historial.filter(h => !h.guia).length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                {historial.filter(h => !h.guia).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* VISTA: Wizard de Creación */}
      {vistaActual === 'wizard' && (
        <div className="max-w-4xl mx-auto">
          {/* Indicador de progreso */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              {[1, 2].map((paso) => (
                <div key={paso} className="flex items-center flex-1">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                    paso === pasoActual
                      ? 'bg-blue-600 text-white'
                      : paso < pasoActual
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {paso < pasoActual ? '✓' : paso}
                  </div>
                  {paso < 2 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      paso < pasoActual ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-gray-800">
                {pasoActual === 1 && 'Paso 1: Selecciona Cliente y Tóner'}
                {pasoActual === 2 && 'Paso 2: Ingresa Datos del Mes'}
              </div>
              <div className="text-gray-600 mt-1">
                {pasoActual === 1 && 'Elige el cliente y el tipo de tóner a enviar'}
                {pasoActual === 2 && 'Ingresa las copias realizadas y el mes correspondiente'}
              </div>
            </div>
          </div>

          {/* Formulario por pasos */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <form onSubmit={handleSubmit} onKeyDown={(e) => {
              // Prevenir submit con Enter en paso 1
              if (e.key === 'Enter' && pasoActual === 1 && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}>

              {/* PASO 1: Cliente y Tóner */}
              {pasoActual === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-3">
                      🏢 Buscar Cliente
                    </label>
                    <input
                      type="text"
                      value={clienteBusqueda}
                      onChange={(e) => setClienteBusqueda(e.target.value)}
                      placeholder="Escribe para buscar cliente..."
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
                    />
                    {clienteBusqueda && (
                      <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                        {clientesFiltrados.map(cliente => (
                          <button
                            key={cliente.id}
                            type="button"
                            onClick={() => {
                              setClienteSeleccionado(cliente.id);
                              setClienteBusqueda(cliente.nombre);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition ${
                              clienteSeleccionado === cliente.id ? 'bg-blue-100 font-semibold' : ''
                            }`}
                          >
                            {cliente.nombre}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {clienteSeleccionado && (
                    <>
                      {pendientesDelCliente > 0 && (
                        <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="text-3xl">⚠️</div>
                            <div>
                              <div className="font-bold text-orange-900 text-lg">
                                Cliente con {pendientesDelCliente} tóner(es) pendientes
                              </div>
                              <div className="text-orange-800 mt-1">
                                Este cliente tiene tóners pendientes de despacho. Considera despacharlos primero o marca como "Envío por Adelantado".
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-3">
                          🖨️ Modelo de Tóner
                        </label>
                        <select
                          value={tonerSeleccionado}
                          onChange={(e) => setTonerSeleccionado(e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Seleccione un modelo de tóner</option>
                          {toners.map(toner => (
                            <option key={toner.id} value={toner.id}>
                              {toner.modelo} (Rendimiento: {formatearNumeroCL(toner.rendimiento)})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-3">
                          🏷️ Tipo de Tóner
                        </label>
                        <select
                          value={tipoTonerSeleccionado}
                          onChange={(e) => setTipoTonerSeleccionado(e.target.value)}
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Seleccione un tipo de tóner</option>
                          {tiposToner.map(tipo => (
                            <option key={tipo.id} value={tipo.id}>
                              {tipo.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* PASO 2: Mes, Tipo de Envío y Datos */}
              {pasoActual === 2 && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                    <div className="font-semibold text-blue-900">
                      Cliente: {clienteNombre}
                    </div>
                    <div className="text-blue-800">
                      Tóner: {tonerNombre} ({tipoTonerNombre})
                    </div>
                  </div>

                  {/* 1. Mes de Envío */}
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-3">
                      📅 Mes del Envío / A Cuenta de Qué Mes Van Estos Tóners
                    </label>
                    <select
                      value={mesSeleccionado}
                      onChange={(e) => setMesSeleccionado(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">Seleccione el mes correspondiente</option>
                      {mesesDelAño.map(mes => (
                        <option key={mes.value} value={mes.value}>
                          {mes.label} {new Date().getFullYear()}
                        </option>
                      ))}
                    </select>
                    <div className="mt-2 text-sm text-gray-600">
                      ℹ️ Selecciona el mes al que corresponderá este envío de tóner
                    </div>

                    {/* Aviso informativo: pendientes que se incluirán automáticamente */}
                    {mesSeleccionado && clienteSeleccionado && tonerSeleccionado && tipoTonerSeleccionado && !esEnvioAdelantado && cantidadPendienteCliente > 0 && (() => {
                      const pendienteInfo = tonersPendientes.find(tp =>
                        tp.cliente_id === clienteSeleccionado &&
                        tp.toner_id === parseInt(tonerSeleccionado) &&
                        tp.tipo_toner_id === parseInt(tipoTonerSeleccionado)
                      );
                      const mesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                      let mesPendienteTexto = '';
                      if (pendienteInfo?.mes_acumulado) {
                        const [año, mes] = pendienteInfo.mes_acumulado.split('-').map(Number);
                        mesPendienteTexto = `${mesNombres[mes - 1]} ${año}`;
                      }

                      return (
                        <div className="mt-4 bg-green-50 border-2 border-green-400 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="text-3xl">✅</div>
                            <div className="flex-1">
                              <div className="font-bold text-green-900 mb-2">
                                Este cliente tiene tóners pendientes — se sumarán automáticamente
                              </div>
                              <div className="text-sm text-green-800 space-y-1">
                                <p>• Hay <strong>{cantidadPendienteCliente}</strong> tóner(es) pendiente(s){mesPendienteTexto ? <> de <strong>{mesPendienteTexto}</strong></> : ''}.</p>
                                <p>• Se incluirán en <strong>este mismo envío</strong>, sumados a lo que corresponde al mes seleccionado.</p>
                                <p className="mt-2 text-xs">
                                  💡 No necesitas crear una solicitud aparte. Abajo verás el desglose completo (mes + pendientes = total) antes de confirmar.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* 2. Tipo de Envío */}
                  {mesSeleccionado && (
                    <div className="space-y-3">
                      <label className="block text-lg font-semibold text-gray-700 mb-2">
                        📦 Tipo de Envío
                      </label>

                      {/* Opción: Envío Normal */}
                      <div
                        onClick={() => setEsEnvioAdelantado(false)}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                          !esEnvioAdelantado
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            checked={!esEnvioAdelantado}
                            onChange={() => setEsEnvioAdelantado(false)}
                            className="mt-1 w-5 h-5"
                          />
                          <div className="flex-1">
                            <div className="font-bold text-lg text-gray-800 mb-1">
                              📊 Envío Normal (Con Consumo del Mes Anterior)
                            </div>
                            <div className="text-sm text-gray-600">
                              El cliente ya tiene contador del mes anterior. Por ejemplo, para enviar tóner en Noviembre,
                              ingresarás el contador de copias de Octubre. El sistema calculará la cantidad necesaria automáticamente.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Opción: Envío Adelantado */}
                      <div
                        onClick={() => setEsEnvioAdelantado(true)}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                          esEnvioAdelantado
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-white hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            checked={esEnvioAdelantado}
                            onChange={() => setEsEnvioAdelantado(true)}
                            className="mt-1 w-5 h-5"
                          />
                          <div className="flex-1">
                            <div className="font-bold text-lg text-blue-900 mb-1">
                              🚀 Envío por Adelantado (Sin Consumo Previo)
                            </div>
                            <div className="text-sm text-blue-800">
                              El cliente NO tiene contador del mes anterior porque estás enviando tóner de forma anticipada.
                              Tú decides cuántos tóners enviar.
                            </div>
                            {esEnvioAdelantado && (
                              <div className="mt-3 bg-blue-100 border border-blue-300 rounded p-3">
                                <div className="text-xs font-semibold text-blue-900 mb-1">
                                  💡 ¿Cuándo usar esta opción?
                                </div>
                                <ul className="text-xs text-blue-800 space-y-1 ml-4">
                                  <li>• El cliente solicitó tóner antes de consumirlo</li>
                                  <li>• Se envía stock preventivo para el siguiente mes</li>
                                  <li>• Es un envío anticipado sin contador previo</li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 3. Datos según tipo de envío */}
                  {mesSeleccionado && !esEnvioAdelantado && (
                    <>
                      <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="text-3xl">📊</div>
                          <div>
                            <div className="font-bold text-blue-900 mb-1">
                              Ingresa el Contador del Mes Anterior
                            </div>
                            <div className="text-sm text-blue-800">
                              Para enviar tóner de <strong>{mesNombre}</strong>, debes ingresar cuántas copias
                              realizó el cliente en el mes anterior. El sistema calculará automáticamente cuántos
                              tóners necesita.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-3">
                          📊 Copias Realizadas en el Mes Anterior
                        </label>
                        <input
                          type="text"
                          value={copiasRealizadas}
                          onChange={(e) => {
                            const valor = e.target.value.replace(/\./g, '');
                            if (/^\d*$/.test(valor)) {
                              setCopiasRealizadas(valor ? formatoMiles(valor) : '');
                            }
                          }}
                          placeholder="Ej: 45.000"
                          className="w-full border-2 border-blue-300 rounded-lg px-4 py-3 text-lg focus:border-blue-500 focus:outline-none"
                        />
                        <div className="mt-2 text-sm text-gray-600">
                          ℹ️ Ingresa el número de copias del mes anterior (se calculará la cantidad automáticamente)
                        </div>
                      </div>

                      {cantidadCalculada >= 0 && copiasRealizadas && (
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400 rounded-lg p-6">
                          <div className="text-center mb-4">
                            <div className="text-gray-700 mb-2 font-semibold">📊 Cálculo Automático de Tóners</div>
                            <div className="text-5xl font-bold text-green-700">
                              {cantidadCalculada}
                            </div>
                            <div className="text-gray-600 mt-2">tóner(es) a enviar</div>
                          </div>

                          <div className="bg-white rounded-lg p-4 text-left space-y-2">
                            <div className="text-sm font-semibold text-gray-700 mb-3">Desglose del cálculo:</div>

                            <div className="flex justify-between items-center border-b pb-2">
                              <span className="text-sm text-gray-600">Copias realizadas:</span>
                              <span className="font-bold text-gray-800">{formatearNumeroCL(desformatearMiles(copiasRealizadas))}</span>
                            </div>

                            <div className="flex justify-between items-center border-b pb-2">
                              <span className="text-sm text-gray-600">Rendimiento esperado por tóner:</span>
                              <span className="font-bold text-gray-800">{formatearNumeroCL(rendimientoEsperado)}</span>
                            </div>

                            <div className="flex justify-between items-center border-b pb-2">
                              <span className="text-sm text-gray-600">Cantidad necesaria (copias ÷ rendimiento):</span>
                              <span className="font-bold text-blue-600">{Math.floor(parseInt(desformatearMiles(copiasRealizadas)) / rendimientoEsperado)}</span>
                            </div>

                            {cantidadPendienteCliente > 0 && (
                              <div className="flex justify-between items-center border-b pb-2">
                                <span className="text-sm text-orange-600">+ Tóners pendientes anteriores (se incluyen aquí):</span>
                                <span className="font-bold text-orange-600">+{cantidadPendienteCliente}</span>
                              </div>
                            )}

                            {enviosAdelantadosDelMes > 0 && (
                              <div className="flex justify-between items-center border-b pb-2">
                                <span className="text-sm text-blue-600">- Adelantados para este mes:</span>
                                <span className="font-bold text-blue-600">-{enviosAdelantadosDelMes}</span>
                              </div>
                            )}

                            <div className="flex justify-between items-center pt-3 bg-green-100 rounded p-2">
                              <span className="text-sm font-bold text-green-800">TOTAL A ENVIAR:</span>
                              <span className="text-2xl font-bold text-green-700">{cantidadCalculada}</span>
                            </div>

                            {cantidadPendienteCliente > 0 && (
                              <div className="mt-3 bg-orange-50 border border-orange-300 rounded-lg p-3 text-center text-sm text-orange-900">
                                📌 Este envío incluye <strong>{Math.max(0, cantidadCalculada - cantidadPendienteCliente + enviosAdelantadosDelMes)}</strong> del mes
                                {' + '}<strong>{cantidadPendienteCliente}</strong> pendiente(s)
                                {' = '}<strong>{cantidadCalculada}</strong> tóner(es) en total, en un solo registro.
                              </div>
                            )}
                          </div>

                          {cantidadCalculada === 0 && (
                            <div className="mt-4 bg-blue-100 border border-blue-300 rounded p-3 text-sm text-blue-800">
                              ℹ️ No se requiere envío porque ya se enviaron suficientes tóners adelantados para este mes.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {mesSeleccionado && esEnvioAdelantado && (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-sky-100 to-blue-100 border-2 border-blue-300 rounded-lg p-5">
                        <div className="flex items-start gap-3">
                          <div className="text-4xl">⏩</div>
                          <div className="flex-1">
                            <div className="font-bold text-blue-900 mb-2 text-lg">
                              Envío por Adelantado Seleccionado
                            </div>
                            <div className="text-sm text-blue-800 space-y-2">
                              <p>✅ Este es un envío preventivo o anticipado para <strong>{mesNombre} {new Date().getFullYear()}</strong></p>
                              <p>✅ No requiere contador del mes anterior</p>
                              <p>✅ La solicitud será enviada al administrador quien decidirá la cantidad exacta a despachar</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">👤</div>
                          <div className="flex-1">
                            <div className="font-bold text-blue-900">El administrador decidirá la cantidad</div>
                            <div className="text-sm text-blue-700">
                              Al continuar, la solicitud será enviada al administrador para que revise y decida cuántos tóners se despacharán por adelantado.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Campo de observaciones */}
                      <div>
                        <label className="block text-lg font-semibold text-gray-700 mb-3">
                          💬 Observaciones {user.role === 'secretaria' && <span className="text-red-600">*</span>}
                        </label>
                        <textarea
                          value={observaciones}
                          onChange={(e) => setObservaciones(e.target.value)}
                          rows="3"
                          placeholder={user.role === 'secretaria' ? "Obligatorio: Indique el motivo del envío adelantado (Ej: Cliente solicitó envío urgente / Envío preventivo / etc.)" : "Opcional: Agregue detalles adicionales sobre esta solicitud"}
                          className={`w-full border-2 rounded-lg px-4 py-3 text-lg focus:outline-none resize-none ${
                            user.role === 'secretaria'
                              ? (observaciones.trim() ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50')
                              : 'border-gray-300 focus:border-blue-400'
                          }`}
                        />
                        <div className="mt-2 flex items-center gap-2">
                          {user.role === 'secretaria' ? (
                            observaciones.trim() ? (
                              <span className="text-sm text-green-700 font-semibold">✅ Observación ingresada</span>
                            ) : (
                              <span className="text-sm text-red-700 font-semibold">⚠️ Las observaciones son obligatorias para secretarias</span>
                            )
                          ) : (
                            <span className="text-sm text-gray-600">ℹ️ Puedes agregar detalles adicionales sobre esta solicitud</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4. Observaciones OBLIGATORIAS (solo para envíos normales) */}
                  {mesSeleccionado && !esEnvioAdelantado && copiasRealizadas && (
                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-3">
                        💬 Observaciones <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        rows="3"
                        placeholder="Ejemplo: Cliente solicitó envío urgente / Envío preventivo para fin de mes / etc."
                        className={`w-full border-2 rounded-lg px-4 py-3 text-lg focus:outline-none resize-none ${
                          observaciones.trim() ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50'
                        }`}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        {observaciones.trim() ? (
                          <span className="text-sm text-green-700 font-semibold">✅ Observación ingresada</span>
                        ) : (
                          <span className="text-sm text-red-700 font-semibold">⚠️ Las observaciones son obligatorias</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Botones de navegación */}
              <div className="flex justify-between mt-8 pt-6 border-t-2 border-gray-200">
                <div>
                  {pasoActual > 1 && (
                    <button
                      type="button"
                      onClick={retrocederPaso}
                      className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition text-lg"
                    >
                      ← Anterior
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetearFormulario}
                    className="px-6 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition text-lg"
                  >
                    🗑️ Cancelar
                  </button>
                  {pasoActual === 1 ? (
                    <button
                      type="button"
                      onClick={avanzarPaso}
                      disabled={!validarPaso1()}
                      className={`px-6 py-3 rounded-lg font-semibold text-lg transition ${
                        !validarPaso1()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Siguiente →
                    </button>
                  ) : (
                    <BotonCargando
                      type="submit"
                      cargando={guardando}
                      textoCargando="Guardando..."
                      disabled={!validarPaso2()}
                      className={`px-8 py-3 rounded-lg font-bold text-lg shadow-lg ${
                        !validarPaso2()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      ✅ Confirmar Envío
                    </BotonCargando>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VISTA: Pendientes de Despacho */}
      {vistaActual === 'pendientes' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              🚚 Tóners Pendientes de Despacho
            </h2>
            <button
              onClick={cargarTonersPendientes}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              🔄 Actualizar
            </button>
          </div>

          {tonersPendientes.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-8xl mb-4">✅</div>
              <div className="text-2xl font-bold text-gray-600 mb-2">¡Todo al día!</div>
              <div className="text-gray-500">No hay tóners pendientes de despacho</div>
            </div>
          ) : (
            <>
              {/* Resumen en cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {tonersPendientes.filter(tp => tp.cantidad_pendiente >= 5).length}
                  </div>
                  <div className="text-red-800 font-medium">🚨 Urgentes (≥5)</div>
                </div>
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {tonersPendientes.filter(tp => tp.cantidad_pendiente >= 3 && tp.cantidad_pendiente < 5).length}
                  </div>
                  <div className="text-orange-800 font-medium">⚠️ Altos (3-4)</div>
                </div>
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {tonersPendientes.reduce((sum, tp) => sum + tp.cantidad_pendiente, 0)}
                  </div>
                  <div className="text-blue-800 font-medium">📊 Total</div>
                </div>
              </div>

              {/* Tabla de pendientes */}
              <TablaScroll>
                <table className="min-w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold text-gray-700">Cliente</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-700">Tóner</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-700">Tipo</th>
                      <th className="px-4 py-3 text-center font-bold text-gray-700">Cantidad</th>
                      <th className="px-4 py-3 text-left font-bold text-gray-700">Desde</th>
                      <th className="px-4 py-3 text-center font-bold text-gray-700">Prioridad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tonersPendientes
                      .sort((a, b) => b.cantidad_pendiente - a.cantidad_pendiente)
                      .map((tp) => {
                        const isUrgent = tp.cantidad_pendiente >= 5;
                        const isHigh = tp.cantidad_pendiente >= 3;

                        return (
                          <tr
                            key={tp.id}
                            className={`border-t hover:bg-gray-50 ${
                              isUrgent ? 'bg-red-50' : isHigh ? 'bg-orange-50' : ''
                            }`}
                          >
                            <td className="px-4 py-3 font-semibold">{tp.clientes?.nombre}</td>
                            <td className="px-4 py-3">{tp.toners?.modelo}</td>
                            <td className="px-4 py-3">{tp.tipos_toner?.nombre}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-2xl font-bold ${
                                isUrgent ? 'text-red-700' : isHigh ? 'text-orange-700' : 'text-yellow-700'
                              }`}>
                                {tp.cantidad_pendiente}
                              </span>
                            </td>
                            <td className="px-4 py-3">{formatearFecha(tp.mes_acumulado)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                isUrgent
                                  ? 'bg-red-200 text-red-900'
                                  : isHigh
                                  ? 'bg-orange-200 text-orange-900'
                                  : 'bg-yellow-200 text-yellow-900'
                              }`}>
                                {isUrgent ? '🚨 URGENTE' : isHigh ? '⚠️ ALTO' : '📋 NORMAL'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </TablaScroll>

              {/* Resumen por cliente */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">📊 Resumen por Cliente</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {resumenPendientes.map((resumen, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg p-4">
                      <div className="font-bold text-gray-800 text-lg mb-2">{resumen.cliente}</div>
                      <div className="text-3xl font-bold text-orange-700 mb-2">
                        {resumen.totalPendientes} tóner(es)
                      </div>
                      <div className="space-y-1">
                        {resumen.detalles.map((detalle, idx2) => (
                          <div key={idx2} className="text-sm text-gray-700">
                            • {detalle.toner} ({detalle.tipo}): <strong>{detalle.cantidad}</strong>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          const pendientesCliente = tonersPendientes.filter(
                            tp => tp.cliente_id === resumen.cliente_id
                          );
                          // Limpiar estados anteriores antes de abrir el modal
                          setCantidadesDespacho({});
                          setGuiasPendientes({});
                          setModalDespachoPendientes({
                            isOpen: true,
                            cliente: { id: resumen.cliente_id, nombre: resumen.cliente },
                            pendientes: pendientesCliente
                          });
                        }}
                        className="mt-3 w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition font-semibold"
                      >
                        🚚 Despachar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* VISTA: Aprobaciones (Solo Admin) */}
      {vistaActual === 'aprobaciones' && puede('aprobar_envios') && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              ✅ Envíos Pendientes de Aprobación
            </h2>
            <button
              onClick={cargarPendientes}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              🔄 Actualizar
            </button>
          </div>

          {pendientes.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-8xl mb-4">✅</div>
              <div className="text-2xl font-bold text-gray-600 mb-2">Todo aprobado!</div>
              <div className="text-gray-500">No hay envíos pendientes de aprobación</div>
            </div>
          ) : (
            <div className="space-y-4">
              {pendientes.map((registro) => (
                <div key={registro.id} className="border-2 border-blue-200 rounded-lg p-4 sm:p-6 bg-blue-50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-600">Cliente</div>
                      <div className="font-bold text-gray-800">{registro.clientes?.nombre}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Tóner</div>
                      <div className="font-bold text-gray-800">{registro.toners?.modelo}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Tipo</div>
                      <div className="font-bold text-gray-800">{registro.tipos_toner?.nombre}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Mes</div>
                      <div className="font-bold text-gray-800">{formatearFecha(registro.mes_correspondiente)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm text-gray-600">Copias Realizadas</div>
                      <div className="text-lg font-bold text-blue-700">
                        {registro.copias_realizadas > 0 ? formatearNumeroCL(registro.copias_realizadas) : 'Envío Adelantado'}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm text-gray-600">Cantidad a Despachar</div>
                      {(() => {
                        const esAdelantadoCard = registro.observaciones?.includes('SOLICITUD ENVÍO ADELANTADO');
                        // Para envíos normales mostramos la cantidad calculada (recomendada, incluye pendientes).
                        // Para adelantados mostramos lo que se haya registrado (el admin decide al aprobar).
                        const cantidadMostrar = esAdelantadoCard
                          ? registro.cantidad_despachada
                          : (registro.cantidad_calculada ?? registro.cantidad_despachada);
                        return (
                          <>
                            <div className="text-2xl font-bold text-green-700">{cantidadMostrar}</div>
                            {!esAdelantadoCard && registro.observaciones?.includes('[DESGLOSE]') && (
                              <div className="text-xs text-orange-600 mt-1">
                                Incluye pendientes anteriores (ver observaciones)
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {registro.observaciones && (() => {
                    const info = parsearObservaciones(registro.observaciones);
                    const etiqueta = etiquetaTipoEnvio(info.tipo);
                    return (
                      <div className="bg-white rounded-lg p-3 mb-4 space-y-2">
                        {/* Etiquetas visuales (reemplazan los códigos internos) */}
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${etiqueta.clases}`}>
                            {etiqueta.icono} {etiqueta.texto}
                          </span>
                          {info.diaEnvio && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                              📅 Envío: {info.diaEnvio}
                            </span>
                          )}
                          {info.desglose && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                              ➕ {info.desglose.delMes} del mes + {info.desglose.pendientes} pendiente(s) = {info.desglose.total}
                            </span>
                          )}
                        </div>
                        {/* Texto real escrito por la persona */}
                        {info.textoUsuario && (
                          <div>
                            <div className="text-sm text-gray-600">Observaciones</div>
                            <div className="text-gray-800">{info.textoUsuario}</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => {
                        const esAdelantado = registro.observaciones?.includes('SOLICITUD ENVÍO ADELANTADO');
                        setModalAprobacion({ isOpen: true, registro });
                        setTipoTonerAprobacion(registro.tipo_toner_id);
                        // Para envíos adelantados, NO prellenar la cantidad - el admin debe decidir.
                        // Para envíos normales, prellenar con la cantidad CALCULADA (incluye pendientes),
                        // que es la recomendada y coincide con el desglose. El admin puede bajarla.
                        setCantidadAprobacion(
                          esAdelantado
                            ? ''
                            : (registro.cantidad_calculada ?? registro.cantidad_despachada).toString()
                        );
                      }}
                      className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition font-bold text-lg"
                    >
                      ✅ Aprobar
                    </button>
                    <BotonCargando
                      cargando={procesandoAprobacion}
                      textoCargando="Procesando..."
                      onClick={async () => {
                        const confirmado = await showConfirm(
                          '¿Rechazar este envío?',
                          'La solicitud quedará registrada como rechazada en el historial.',
                          { peligro: true, confirmText: 'Sí, rechazar' }
                        );
                        if (confirmado) {
                          rechazarEnvio(registro);
                        }
                      }}
                      className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 font-bold text-lg"
                    >
                      ❌ Rechazar
                    </BotonCargando>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VISTA: Historial */}
      {vistaActual === 'historial' && (
        <div className="space-y-6">
          {/* Alerta para secretaria: envíos sin guía */}
          {user.role === 'secretaria' && historial.filter(h => !h.guia).length > 0 && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-orange-400 rounded-lg p-6 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="text-5xl">⚠️</div>
                <div className="flex-1">
                  <div className="text-2xl font-bold text-orange-900 mb-2">
                    ¡Acción Requerida! Tienes {historial.filter(h => !h.guia).length} envío(s) sin guía de despacho
                  </div>
                  <div className="text-orange-800 mb-4">
                    Los envíos aprobados por el administrador necesitan que agregues el número de guía de despacho.
                    <strong> Busca los envíos destacados en AMARILLO</strong> en la tabla de abajo y haz clic en "Agregar Guía".
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-orange-300">
                    <div className="font-semibold text-orange-900 mb-2">📝 Instrucciones:</div>
                    <ol className="list-decimal list-inside text-sm text-orange-800 space-y-1">
                      <li>Busca las filas con fondo <strong>AMARILLO</strong> en la tabla</li>
                      <li>Haz clic en el botón <strong>"➕ Agregar Guía"</strong></li>
                      <li>Ingresa el número de guía de despacho</li>
                      <li>Confirma y listo - el envío quedará marcado como despachado</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                📊 Historial de Envíos Aprobados
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={cargarHistorial}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  🔄 Actualizar
                </button>
              </div>
            </div>

            {historial.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-8xl mb-4">📭</div>
                <div className="text-2xl font-bold text-gray-600 mb-2">Sin historial</div>
                <div className="text-gray-500">No hay envíos registrados aún</div>
              </div>
            ) : (
              <>
                {/* Leyenda de colores para secretaria */}
                {user.role === 'secretaria' && (
                  <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center bg-gray-50 p-4 rounded-lg">
                    <div className="font-semibold text-gray-700">Leyenda:</div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-yellow-100 border-2 border-yellow-400 rounded flex-shrink-0"></div>
                      <span className="text-sm">Necesita Guía (¡Agregar!)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-50 border-2 border-green-300 rounded flex-shrink-0"></div>
                      <span className="text-sm">Con Guía (Completo)</span>
                    </div>
                  </div>
                )}

                <TablaScroll>
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold text-gray-700">Fecha</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700">Cliente</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700">Tóner</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700">Tipo</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700">Mes</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700">Día Envío</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700">Tipo Solicitud</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700">Copias</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700">Cantidad</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700">Estado</th>
                        {user.role === 'secretaria' && (
                          <th className="px-4 py-3 text-center font-bold text-gray-700">Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {historial.map((envio) => {
                        const necesitaGuia = !envio.guia && user.role === 'secretaria';

                        // Determinar tipo de solicitud
                        const esPendientes = envio.observaciones?.includes('SOLICITUD DESPACHO PENDIENTES');
                        const esAdelantado = envio.copias_realizadas === 0 && !esPendientes;
                        const esNormal = envio.copias_realizadas > 0;

                        let tipoSolicitud = { icono: '', texto: '', color: '' };
                        if (esPendientes) {
                          tipoSolicitud = { icono: '🔄', texto: 'Pendientes', color: 'bg-orange-100 text-orange-800' };
                        } else if (esAdelantado) {
                          tipoSolicitud = { icono: '⏩', texto: 'Adelantado', color: 'bg-blue-100 text-blue-800' };
                        } else if (esNormal) {
                          tipoSolicitud = { icono: '📅', texto: 'Normal', color: 'bg-green-100 text-green-800' };
                        }

                        // Extraer día de envío de las observaciones
                        const diaEnvioMatch = envio.observaciones?.match(/📅 DÍA DE ENVÍO:\s*(\w+)/);
                        const diaEnvio = diaEnvioMatch ? diaEnvioMatch[1] : null;

                        return (
                          <tr
                            key={envio.id}
                            className={`border-t hover:bg-gray-50 ${
                              necesitaGuia ? 'bg-yellow-100 border-yellow-400 border-2' : envio.guia ? 'bg-green-50' : ''
                            }`}
                          >
                            <td className="px-4 py-3 text-sm">
                              {new Date(envio.creado_el).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 font-semibold">{envio.clientes?.nombre}</td>
                            <td className="px-4 py-3">{envio.toners?.modelo}</td>
                            <td className="px-4 py-3">{envio.tipos_toner?.nombre}</td>
                            <td className="px-4 py-3">{formatearFecha(envio.mes_correspondiente)}</td>
                            <td className="px-4 py-3 text-center">
                              {diaEnvio ? (
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200">
                                  📅 {diaEnvio}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${tipoSolicitud.color}`}>
                                {tipoSolicitud.icono} {tipoSolicitud.texto}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {envio.copias_realizadas > 0 ? formatearNumeroCL(envio.copias_realizadas) : '—'}
                            </td>
                            <td className="px-4 py-3 text-center font-bold text-blue-700">
                              {envio.cantidad_despachada}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <EnvioEstadoStepper
                                  estado={envio.resultado || 'aprobado'}
                                  tieneGuia={!!envio.guia}
                                  compacto
                                />
                                {envio.guia && (
                                  <span className="text-xs text-green-700 font-semibold mt-1">Guía: {envio.guia}</span>
                                )}
                              </div>
                            </td>
                            {user.role === 'secretaria' && (
                              <td className="px-4 py-3 text-center">
                                {!envio.guia ? (
                                  <button
                                    onClick={() => setModalGuia({ isOpen: true, registro: envio })}
                                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition font-bold shadow-lg animate-pulse"
                                  >
                                    ➕ Agregar Guía
                                  </button>
                                ) : (
                                  <span className="text-green-600 text-sm">✓ Completo</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TablaScroll>
              </>
            )}
          </div>
        </div>
      )}

      {/* MODAL: Aprobación (Admin) */}
      {modalAprobacion.isOpen && (() => {
        const esEnvioAdelantado = modalAprobacion.registro?.observaciones?.includes('SOLICITUD ENVÍO ADELANTADO');
        const esDespachoPendientes = modalAprobacion.registro?.observaciones?.includes('SOLICITUD DESPACHO PENDIENTES');

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">✅ Aprobar Envío</h3>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Cliente
                  </label>
                  <div className="text-lg font-bold text-gray-800">
                    {clientes.find(c => c.id === modalAprobacion.registro?.cliente_id)?.nombre}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tóner Solicitado
                  </label>
                  <div className="text-lg font-bold text-gray-800">
                    {toners.find(t => t.id === modalAprobacion.registro?.toner_id)?.modelo}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Envío
                  </label>
                  <div className="text-lg font-bold text-blue-700">
                    {modalAprobacion.registro?.copias_realizadas > 0
                      ? `Envío Normal - ${formatearNumeroCL(modalAprobacion.registro.copias_realizadas)} copias`
                      : esDespachoPendientes
                      ? 'Envío Pendiente'
                      : esEnvioAdelantado
                      ? 'Envío Adelantado'
                      : 'Envío Adelantado'}
                  </div>
                </div>

                {/* Solo mostrar rendimiento y cantidad calculada para envíos normales */}
                {!esEnvioAdelantado && modalAprobacion.registro?.copias_realizadas > 0 && (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Rendimiento Esperado
                      </label>
                      <div className="text-lg font-bold text-blue-700">
                        {modalAprobacion.registro?.rendimiento_esperado
                          ? formatearNumeroCL(modalAprobacion.registro.rendimiento_esperado)
                          : 'N/A'} copias/tóner
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Cantidad Calculada (Recomendada)
                      </label>
                      <div className="text-2xl font-bold text-blue-600">
                        {modalAprobacion.registro?.cantidad_calculada} tóner(es)
                      </div>
                    </div>
                  </>
                )}

                {/* Para envíos adelantados, mostrar un mensaje informativo */}
                {esEnvioAdelantado && (
                  <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <div className="text-2xl">⏩</div>
                      <div className="flex-1">
                        <div className="font-bold text-blue-900 mb-1">Envío por Adelantado</div>
                        <div className="text-sm text-blue-800">
                          Este es un envío adelantado solicitado por la secretaria. Tú decides cuántos tóners despachar según las necesidades del cliente.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de Tóner a Despachar
                  </label>
                  <select
                    value={tipoTonerAprobacion}
                    onChange={(e) => setTipoTonerAprobacion(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-3 py-2"
                  >
                    {tiposToner.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {esEnvioAdelantado ? '⚡ Cantidad a Despachar (Tú decides) *' : 'Cantidad a Despachar *'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={cantidadAprobacion}
                    onChange={(e) => setCantidadAprobacion(e.target.value)}
                    placeholder={esEnvioAdelantado ? "Ingresa cuántos tóners despachar" : "Ingrese cantidad"}
                    className={`w-full border-2 rounded-lg px-3 py-2 text-lg ${
                      esEnvioAdelantado
                        ? 'border-blue-400 bg-blue-50 focus:border-blue-600'
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                  />
                  {!esEnvioAdelantado && modalAprobacion.registro?.copias_realizadas > 0 && (
                    <div className="text-xs text-gray-600 mt-1">
                      ℹ️ Si envías menos de lo calculado, la diferencia quedará como pendiente
                    </div>
                  )}
                  {esEnvioAdelantado && (
                    <div className="text-xs text-blue-700 font-semibold mt-2 bg-blue-100 p-2 rounded">
                      ⚠️ IMPORTANTE: Debes decidir cuántos tóners despachar según las necesidades del cliente. No hay cantidad predeterminada.
                    </div>
                  )}
                </div>

                {/* Campo de día de envío (opcional, para coordinación) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    📅 Día de Envío (Opcional)
                  </label>
                  <select
                    value={diaEnvio}
                    onChange={(e) => setDiaEnvio(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-lg"
                  >
                    <option value="">-- Seleccionar día --</option>
                    <option value="Lunes">Lunes</option>
                    <option value="Martes">Martes</option>
                    <option value="Miércoles">Miércoles</option>
                    <option value="Jueves">Jueves</option>
                    <option value="Viernes">Viernes</option>
                    <option value="Sábado">Sábado</option>
                    <option value="Domingo">Domingo</option>
                  </select>
                  <div className="text-xs text-gray-600 mt-1">
                    ℹ️ Selecciona el día en que se enviará el tóner para coordinar con la secretaria
                  </div>
                </div>
              </div>

            <div className="flex gap-3">
              <BotonCargando
                cargando={procesandoAprobacion}
                textoCargando="Aprobando..."
                onClick={() => {
                  aprobarEnvio(modalAprobacion.registro);
                }}
                className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 font-bold"
              >
                ✅ Aprobar
              </BotonCargando>
              <button
                onClick={() => {
                  setModalAprobacion({ isOpen: false, registro: null });
                  setTipoTonerAprobacion('');
                  setCantidadAprobacion('');
                  setDiaEnvio(''); // Limpiar día de envío
                }}
                disabled={procesandoAprobacion}
                className="flex-1 bg-gray-400 text-white px-4 py-3 rounded-lg hover:bg-gray-500 transition font-bold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* MODAL: Agregar Guía (Secretaria) */}
      {modalGuia.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-3">📦</div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Agregar Guía de Despacho</h3>
              <p className="text-gray-600">Este paso es muy importante para completar el envío</p>
            </div>

            {/* Información del envío */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
              <div className="font-semibold text-blue-900 mb-2">📋 Información del Envío:</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-blue-700">Cliente:</span>{' '}
                  <span className="font-semibold">{modalGuia.registro?.clientes?.nombre}</span>
                </div>
                <div>
                  <span className="text-blue-700">Tóner:</span>{' '}
                  <span className="font-semibold">{modalGuia.registro?.toners?.modelo}</span>
                </div>
                <div>
                  <span className="text-blue-700">Cantidad:</span>{' '}
                  <span className="font-semibold text-green-700 text-lg">{modalGuia.registro?.cantidad_despachada}</span>
                </div>
                <div>
                  <span className="text-blue-700">Mes:</span>{' '}
                  <span className="font-semibold">{modalGuia.registro?.mes_correspondiente && formatearFecha(modalGuia.registro.mes_correspondiente)}</span>
                </div>
              </div>
            </div>

            {/* Instrucciones */}
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="text-3xl">💡</div>
                <div>
                  <div className="font-semibold text-yellow-900 mb-1">¿Qué es la guía de despacho?</div>
                  <div className="text-sm text-yellow-800">
                    Es el número de seguimiento del envío (Ej: GD-12345, Envío-2024-001, etc.).
                    Este número te permite rastrear el paquete y confirmar que fue enviado al cliente.
                  </div>
                </div>
              </div>
            </div>

            {/* Campo de entrada */}
            <div className="mb-6">
              <label className="block text-lg font-bold text-gray-700 mb-3">
                🔢 Ingresa el Número de Guía de Despacho
              </label>
              <input
                type="text"
                value={guiaDespacho}
                onChange={(e) => setGuiaDespacho(e.target.value)}
                placeholder="Ejemplo: GD-12345 o Envío-2024-001"
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-4 text-xl font-semibold focus:border-green-500 focus:outline-none"
                autoFocus
              />
              <div className="mt-2 text-sm text-gray-600">
                ℹ️ Asegúrate de que el número sea correcto antes de guardar
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3">
              <button
                onClick={agregarGuia}
                disabled={!guiaDespacho.trim()}
                className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition ${
                  guiaDespacho.trim()
                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                ✅ Guardar y Marcar como Despachado
              </button>
              <button
                onClick={() => {
                  setModalGuia({ isOpen: false, registro: null });
                  setGuiaDespacho('');
                }}
                className="px-6 py-4 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition font-bold text-lg"
              >
                Cancelar
              </button>
            </div>

            <div className="mt-4 text-center text-sm text-gray-600">
              Al guardar, el envío quedará marcado como ✅ <strong>Despachado</strong>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Solicitar Despacho de Pendientes (Secretaria) */}
      {modalDespachoPendientes.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              📋 Solicitar Despacho de Pendientes
            </h3>
            <p className="text-gray-600 mb-4">
              Cliente: <strong>{modalDespachoPendientes.cliente?.nombre}</strong>
            </p>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <div className="text-2xl">ℹ️</div>
                <div className="text-sm text-blue-800">
                  <strong>Flujo de despacho:</strong>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>Revisa los tóners pendientes del cliente</li>
                    <li>Envía la solicitud al administrador</li>
                    <li>El administrador decidirá la cantidad exacta a despachar</li>
                    <li>Una vez aprobada, podrás agregar la guía de despacho</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {tonersPendientes
                .filter(tp => tp.cliente_id === modalDespachoPendientes.cliente?.id)
                .map(tp => (
                  <div key={tp.id} className="border-2 border-orange-300 rounded-lg p-4 bg-gradient-to-r from-orange-50 to-yellow-50">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="font-bold text-lg text-gray-800">{tp.toners?.modelo}</div>
                        <div className="text-gray-600 text-sm">{tp.tipos_toner?.nombre}</div>
                        <div className="text-xs text-gray-500 mt-1">📅 {formatearFecha(tp.mes_acumulado)}</div>
                      </div>
                      <div className="text-right bg-orange-600 text-white px-4 py-2 rounded-lg">
                        <div className="text-xs font-semibold">Pendientes</div>
                        <div className="text-3xl font-bold">{tp.cantidad_pendiente}</div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="text-3xl">👤</div>
                <div className="flex-1">
                  <div className="font-bold text-blue-900">El administrador decidirá</div>
                  <div className="text-sm text-blue-700">
                    Al enviar esta solicitud, el administrador revisará y decidirá la cantidad exacta a despachar para cada tóner.
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={solicitarDespachoPendientes}
                className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition font-bold text-lg"
              >
                ✅ Enviar Todos los Pendientes a Revisión
              </button>
              <button
                onClick={() => {
                  setModalDespachoPendientes({ isOpen: false, cliente: null, pendientes: [] });
                  setCantidadesDespacho({});
                  setGuiasPendientes({});
                }}
                className="flex-1 bg-gray-400 text-white px-4 py-3 rounded-lg hover:bg-gray-500 transition font-bold"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
